import React from 'react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, CheckCircle, Zap, Users, Link as LinkIcon, BarChart3 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function GettingStartedPage() {
  const navigate = useNavigate();
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/resources');
    }
  };
  return <div className="bg-white" data-id="element-1034">
      {/* Header */}
      <div className="bg-gray-900 py-12 lg:py-16" data-id="element-1035">
        <Container data-id="element-1036">
          <button onClick={handleGoBack} className="flex items-center text-gray-300 hover:text-white mb-6 transition-colors" data-id="element-1037">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-1038" />
            Back
          </button>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4" data-id="element-1039">
            Getting Started with Flowsense
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl" data-id="element-1040">
            Your complete guide to setting up Flowsense and running your first
            flow analysis in under 5 minutes.
          </p>
        </Container>
      </div>

      {/* Content */}
      <article className="py-16 lg:py-24" data-id="element-1041">
        <Container size="md" data-id="element-1042">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12" data-id="element-1043">
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1044">
              Welcome to Flowsense! This guide will walk you through everything
              you need to know to get started, from creating your account to
              analyzing your first Klaviyo flow. By the end of this guide,
              you'll have Flowsense connected to your Klaviyo account and be
              viewing actionable recommendations.
            </p>
          </div>

          {/* What You'll Learn */}
          <section className="mb-16" data-id="element-1045">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1046">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-1047">
              <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-500" data-id="element-1048">
                <h3 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center" data-id="element-1049">
                  <Users className="h-5 w-5 mr-2" data-id="element-1050" />
                  Account Setup
                </h3>
                <ul className="space-y-2 text-indigo-800 text-sm" data-id="element-1051">
                  <li data-id="element-1052">• Creating your Flowsense account</li>
                  <li data-id="element-1053">• Adding your first client</li>
                  <li data-id="element-1054">• Understanding the dashboard</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500" data-id="element-1055">
                <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center" data-id="element-1056">
                  <LinkIcon className="h-5 w-5 mr-2" data-id="element-1057" />
                  Klaviyo Integration
                </h3>
                <ul className="space-y-2 text-purple-800 text-sm" data-id="element-1058">
                  <li data-id="element-1059">• Connecting your Klaviyo account</li>
                  <li data-id="element-1060">• Understanding OAuth permissions</li>
                  <li data-id="element-1061">• Verifying the connection</li>
                </ul>
              </div>

              <div className="bg-pink-50 p-6 rounded-lg border-l-4 border-pink-500" data-id="element-1062">
                <h3 className="text-lg font-semibold text-pink-900 mb-3 flex items-center" data-id="element-1063">
                  <Zap className="h-5 w-5 mr-2" data-id="element-1064" />
                  First Flow Analysis
                </h3>
                <ul className="space-y-2 text-pink-800 text-sm" data-id="element-1065">
                  <li data-id="element-1066">• Viewing your flow list</li>
                  <li data-id="element-1067">• Understanding health scores</li>
                  <li data-id="element-1068">• Exploring flow details</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500" data-id="element-1069">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center" data-id="element-1070">
                  <BarChart3 className="h-5 w-5 mr-2" data-id="element-1071" />
                  AI Recommendations
                </h3>
                <ul className="space-y-2 text-blue-800 text-sm" data-id="element-1072">
                  <li data-id="element-1073">• Reading AI-generated insights</li>
                  <li data-id="element-1074">• Understanding priority levels</li>
                  <li data-id="element-1075">• Taking your first action</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Step 1: Create Account & Add Client */}
          <section className="mb-16" data-id="element-1076">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1077">
              Step 1: Create Your Account & Add Your First Client
            </h2>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-lg mb-8" data-id="element-1078">
              <div className="flex items-start mb-6" data-id="element-1079">
                <span className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1080">
                  1
                </span>
                <div className="flex-grow" data-id="element-1081">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1082">
                    Sign Up for Flowsense
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1083">
                    Visit{' '}
                    <a href="https://go.useflowsense.com/signup" className="text-indigo-600 font-medium hover:underline" data-id="element-1084">
                      go.useflowsense.com/signup
                    </a>{' '}
                    and create your account using your email address.
                  </p>
                  <ul className="space-y-2 text-gray-700" data-id="element-1085">
                    <li className="flex items-start" data-id="element-1086">
                      <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1087" />
                      <span data-id="element-1088">Enter your email and create a secure password</span>
                    </li>
                    <li className="flex items-start" data-id="element-1089">
                      <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1090" />
                      <span data-id="element-1091">Verify your email address (check your inbox)</span>
                    </li>
                    <li className="flex items-start" data-id="element-1092">
                      <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1093" />
                      <span data-id="element-1094">
                        Complete your profile with your name and company
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-lg mb-8" data-id="element-1095">
              <div className="flex items-start mb-6" data-id="element-1096">
                <span className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1097">
                  2
                </span>
                <div className="flex-grow" data-id="element-1098">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1099">
                    Add Your First Client
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1100">
                    After logging in, you'll be prompted to add your first
                    client. This could be your own brand or a client you manage.
                  </p>
                  <ul className="space-y-2 text-gray-700 mb-6" data-id="element-1101">
                    <li className="flex items-start" data-id="element-1102">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1103" />
                      <span data-id="element-1104">
                        <strong data-id="element-1105">Client Name:</strong> Enter the brand or company
                        name (e.g., "Acme Corporation")
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1106">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1107" />
                      <span data-id="element-1108">
                        <strong data-id="element-1109">Website URL:</strong> Add their website URL for
                        brand voice analysis
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1110">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1111" />
                      <span data-id="element-1112">
                        <strong data-id="element-1113">Notes (Optional):</strong> Add any relevant
                        notes about the client
                      </span>
                    </li>
                  </ul>

                  {/* Featured Image 1 */}
                  <div className="mb-4" data-id="element-1114">
                    <img src="https://cdn.magicpatterns.com/uploads/gSv168PsR6XtBFzNzk6pMk/1a._getting-started_client.png" alt="Add New Client Modal" className="w-full rounded-lg shadow-lg border border-gray-200" data-id="element-1115" />
                    <p className="text-sm text-gray-500 mt-2 text-center italic" data-id="element-1116">
                      The "Add New Client" modal where you'll enter client
                      details
                    </p>
                  </div>

                  <div className="bg-white p-4 rounded border border-purple-200 mt-4" data-id="element-1117">
                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1118">
                      Why Website URL Matters:
                    </h4>
                    <p className="text-sm text-gray-700" data-id="element-1119">
                      Flowsense automatically crawls the website to learn the
                      brand's colors, fonts, tone, and personality. This ensures
                      all AI-generated recommendations match the brand's unique
                      voice and style.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4" data-id="element-1120">
              <p className="text-sm text-blue-800" data-id="element-1121">
                <strong data-id="element-1122">For Agencies:</strong> You can add unlimited clients.
                Each client's data is completely isolated, and you can manage
                team access permissions per client.
              </p>
            </div>
          </section>

          {/* Step 2: Connect Klaviyo */}
          <section className="mb-16" data-id="element-1123">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1124">
              Step 2: Connect Your Klaviyo Account
            </h2>

            <p className="text-lg text-gray-600 mb-6" data-id="element-1125">
              Once you've added a client, the next step is connecting their
              Klaviyo account. Flowsense uses secure OAuth 2.0 authentication,
              so you never need to share API keys or passwords.
            </p>

            <div className="bg-gradient-to-r from-pink-50 to-red-50 p-8 rounded-lg mb-8" data-id="element-1126">
              <div className="flex items-start mb-6" data-id="element-1127">
                <span className="flex-shrink-0 w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1128">
                  3
                </span>
                <div className="flex-grow" data-id="element-1129">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1130">
                    Initiate Klaviyo Connection
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1131">
                    After creating your client, you'll be taken to the Settings
                    page where you can connect Klaviyo.
                  </p>
                  <ol className="space-y-3 text-gray-700 mb-6" data-id="element-1132">
                    <li className="flex items-start" data-id="element-1133">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1134">
                        1
                      </span>
                      <div data-id="element-1135">
                        Navigate to{' '}
                        <strong data-id="element-1136">
                          Settings → Integrations → Klaviyo Connections
                        </strong>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1137">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1138">
                        2
                      </span>
                      <div data-id="element-1139">
                        Click the <strong data-id="element-1140">"+ Add Connection"</strong> button
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1141">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1142">
                        3
                      </span>
                      <div data-id="element-1143">
                        You'll be redirected to Klaviyo's authorization page
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1144">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1145">
                        4
                      </span>
                      <div data-id="element-1146">
                        Log in to your Klaviyo account (if not already logged
                        in)
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1147">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1148">
                        5
                      </span>
                      <div data-id="element-1149">Review the permissions Flowsense is requesting</div>
                    </li>
                    <li className="flex items-start" data-id="element-1150">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1151">
                        6
                      </span>
                      <div data-id="element-1152">
                        Click <strong data-id="element-1153">"Allow"</strong> to grant access
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1154">
                      <span className="flex-shrink-0 w-6 h-6 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-1155">
                        7
                      </span>
                      <div data-id="element-1156">
                        You'll be redirected back to Flowsense with a
                        "Connected" status
                      </div>
                    </li>
                  </ol>

                  {/* Featured Image 2 */}
                  <div className="mb-4" data-id="element-1157">
                    <img src="https://cdn.magicpatterns.com/uploads/aEJ4ebSfboXWAJkEdRGuwW/1b._getting-started_integration.png" alt="Klaviyo Integration Settings" className="w-full rounded-lg shadow-lg border border-gray-200" data-id="element-1158" />
                    <p className="text-sm text-gray-500 mt-2 text-center italic" data-id="element-1159">
                      The Integrations page showing connected Klaviyo accounts
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 p-6 rounded-lg mb-6" data-id="element-1160">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1161">
                What Permissions Does Flowsense Request?
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-1162">
                Flowsense requests <strong data-id="element-1163">read-only access</strong> to the
                following data:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-1164">
                <li className="flex items-start" data-id="element-1165">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1166" />
                  <div data-id="element-1167">
                    <strong data-id="element-1168">flows:read</strong> – Read flow structure and
                    configuration
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1169">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1170" />
                  <div data-id="element-1171">
                    <strong data-id="element-1172">templates:read</strong> – Read email templates and
                    HTML content
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1173">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1174" />
                  <div data-id="element-1175">
                    <strong data-id="element-1176">metrics:read</strong> – Read performance data
                    (opens, clicks, revenue)
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1177">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1178" />
                  <div data-id="element-1179">
                    <strong data-id="element-1180">events:read</strong> – Read event data for trigger
                    analysis
                  </div>
                </li>
              </ul>
              <div className="mt-4 bg-green-50 p-4 rounded border border-green-200" data-id="element-1181">
                <p className="text-sm text-green-800" data-id="element-1182">
                  <strong data-id="element-1183">Important:</strong> Flowsense can only <em data-id="element-1184">read</em>{' '}
                  your data. We cannot modify, delete, or send emails on your
                  behalf. Your Klaviyo account remains completely secure.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1185">
              <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1186">
                Data Sync Schedule
              </h3>
              <p className="text-gray-700 mb-3" data-id="element-1187">
                Once connected, Flowsense automatically syncs your flow data
                every <strong data-id="element-1188">6 hours</strong>. You can also trigger a manual
                sync at any time by clicking the refresh icon next to your
                connection.
              </p>
              <p className="text-sm text-gray-600" data-id="element-1189">
                The initial sync typically takes 2-5 minutes depending on how
                many flows you have. Subsequent syncs are faster as we only
                update changed data.
              </p>
            </div>
          </section>

          {/* Step 3: View Your Flows */}
          <section className="mb-16" data-id="element-1190">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1191">
              Step 3: View and Analyze Your Flows
            </h2>

            <p className="text-lg text-gray-600 mb-6" data-id="element-1192">
              After your Klaviyo account is connected and the initial sync
              completes, you're ready to explore your flows and see AI-powered
              insights.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-lg mb-8" data-id="element-1193">
              <div className="flex items-start mb-6" data-id="element-1194">
                <span className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1195">
                  4
                </span>
                <div className="flex-grow" data-id="element-1196">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1197">
                    Navigate to the Flows Page
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1198">
                    Click <strong data-id="element-1199">"Flows"</strong> in the left sidebar to see
                    all your Klaviyo flows in one organized dashboard.
                  </p>

                  {/* Featured Image 3 */}
                  <div className="mb-6" data-id="element-1200">
                    <img src="https://cdn.magicpatterns.com/uploads/wumuMFUsyeUNh2UzmrnePa/1c._getting-started_flows.png" alt="Flows Dashboard" className="w-full rounded-lg shadow-lg border border-gray-200" data-id="element-1201" />
                    <p className="text-sm text-gray-500 mt-2 text-center italic" data-id="element-1202">
                      The Flows dashboard showing all your Klaviyo flows with
                      health scores
                    </p>
                  </div>

                  <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1203">
                    Understanding the Flows Dashboard
                  </h4>
                  <p className="text-gray-700 mb-4" data-id="element-1204">
                    The Flows page displays all your flows in a sortable,
                    filterable table with key information at a glance:
                  </p>

                  <div className="space-y-4" data-id="element-1205">
                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1206">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1207">
                        Flow Name & Description
                      </h5>
                      <p className="text-sm text-gray-700" data-id="element-1208">
                        The name of your flow (e.g., "Welcome Series", "Cart
                        Abandonment") with a brief description of its purpose.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1209">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1210">
                        Client
                      </h5>
                      <p className="text-sm text-gray-700" data-id="element-1211">
                        Which client this flow belongs to (useful for agencies
                        managing multiple clients).
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1212">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1213">
                        Trigger Type
                      </h5>
                      <p className="text-sm text-gray-700" data-id="element-1214">
                        The event that starts the flow: WELCOME, CART ABANDON,
                        BROWSE ABANDON, POST PURCHASE, WIN BACK, BIRTHDAY, or
                        CUSTOM.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1215">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1216">
                        Structure
                      </h5>
                      <p className="text-sm text-gray-700" data-id="element-1217">
                        Visual icons showing the flow's structure: emails, SMS
                        messages, delays, splits, and conditional logic.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1218">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1219">
                        Health Score (0-100)
                      </h5>
                      <p className="text-sm text-gray-700 mb-2" data-id="element-1220">
                        A calculated score showing overall flow performance and
                        optimization level. Color-coded for quick
                        identification:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1 ml-4" data-id="element-1221">
                        <li data-id="element-1222">
                          •{' '}
                          <span className="text-red-600 font-semibold" data-id="element-1223">
                            Red (0-49):
                          </span>{' '}
                          Critical issues
                        </li>
                        <li data-id="element-1224">
                          •{' '}
                          <span className="text-yellow-600 font-semibold" data-id="element-1225">
                            Amber (50-74):
                          </span>{' '}
                          Needs improvement
                        </li>
                        <li data-id="element-1226">
                          •{' '}
                          <span className="text-green-600 font-semibold" data-id="element-1227">
                            Green (75-100):
                          </span>{' '}
                          Healthy
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1228">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1229">
                        Status
                      </h5>
                      <p className="text-sm text-gray-700" data-id="element-1230">
                        Whether the flow is "active" (currently running) or
                        "draft" (not yet live).
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded border border-blue-200" data-id="element-1231">
                      <h5 className="font-semibold text-gray-900 mb-2" data-id="element-1232">
                        Actions
                      </h5>
                      <p className="text-sm text-gray-700" data-id="element-1233">
                        Click "View" to see detailed analysis, recommendations,
                        and performance metrics for that specific flow.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-500 mb-6" data-id="element-1234">
              <h4 className="font-semibold text-indigo-900 mb-2" data-id="element-1235">
                Pro Tip: Use Filters
              </h4>
              <p className="text-indigo-800 text-sm" data-id="element-1236">
                Use the filter dropdowns at the top to narrow down flows by
                client, trigger type, or status. This is especially helpful if
                you're managing multiple clients or have many flows.
              </p>
            </div>
          </section>

          {/* Step 4: Explore Flow Details */}
          <section className="mb-16" data-id="element-1237">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1238">
              Step 4: Explore Flow Details & Recommendations
            </h2>

            <p className="text-lg text-gray-600 mb-6" data-id="element-1239">
              Click "View" on any flow to see a detailed analysis with
              AI-generated recommendations.
            </p>

            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-8 rounded-lg mb-8" data-id="element-1240">
              <div className="flex items-start mb-6" data-id="element-1241">
                <span className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1242">
                  5
                </span>
                <div className="flex-grow" data-id="element-1243">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1244">
                    Flow Detail Page Overview
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1245">
                    The flow detail page is divided into several tabs, each
                    providing different insights:
                  </p>

                  <div className="space-y-4" data-id="element-1246">
                    <div className="bg-white p-5 rounded-lg border-l-4 border-green-500" data-id="element-1247">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center" data-id="element-1248">
                        <BarChart3 className="h-5 w-5 text-green-600 mr-2" data-id="element-1249" />
                        Overview Tab
                      </h4>
                      <p className="text-gray-700 mb-3" data-id="element-1250">
                        High-level performance metrics for the last 30 days:
                      </p>
                      <ul className="space-y-2 text-sm text-gray-700" data-id="element-1251">
                        <li className="flex items-start" data-id="element-1252">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1253" />
                          <span data-id="element-1254">
                            <strong data-id="element-1255">Sends:</strong> Total number of emails sent
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1256">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1257" />
                          <span data-id="element-1258">
                            <strong data-id="element-1259">Open Rate:</strong> Percentage of recipients
                            who opened
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1260">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1261" />
                          <span data-id="element-1262">
                            <strong data-id="element-1263">Click Rate:</strong> Percentage who clicked
                            a link
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1264">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1265" />
                          <span data-id="element-1266">
                            <strong data-id="element-1267">Revenue:</strong> Total revenue attributed
                            to this flow
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-5 rounded-lg border-l-4 border-blue-500" data-id="element-1268">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center" data-id="element-1269">
                        <Zap className="h-5 w-5 text-blue-600 mr-2" data-id="element-1270" />
                        Structure Tab
                      </h4>
                      <p className="text-gray-700 mb-2 text-sm" data-id="element-1271">
                        Visual diagram of your flow's automation structure
                        showing all emails, delays, splits, and conditional
                        logic in an easy-to-understand format.
                      </p>
                    </div>

                    <div className="bg-white p-5 rounded-lg border-l-4 border-purple-500" data-id="element-1272">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center" data-id="element-1273">
                        <BarChart3 className="h-5 w-5 text-purple-600 mr-2" data-id="element-1274" />
                        Messages Tab
                      </h4>
                      <p className="text-gray-700 mb-2 text-sm" data-id="element-1275">
                        Performance metrics for each individual email and SMS in
                        the flow, allowing you to identify which messages
                        perform best and which need improvement.
                      </p>
                    </div>

                    <div className="bg-white p-5 rounded-lg border-l-4 border-pink-500" data-id="element-1276">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center" data-id="element-1277">
                        <Zap className="h-5 w-5 text-pink-600 mr-2" data-id="element-1278" />
                        Recommendations Tab (Most Important!)
                      </h4>
                      <p className="text-gray-700 mb-3 text-sm" data-id="element-1279">
                        AI-generated recommendations prioritized by impact. This
                        is where you'll spend most of your time.
                      </p>
                      <div className="bg-pink-50 p-3 rounded mt-3" data-id="element-1280">
                        <p className="text-sm text-pink-800 mb-2" data-id="element-1281">
                          <strong data-id="element-1282">Each recommendation includes:</strong>
                        </p>
                        <ul className="space-y-1 text-sm text-pink-800" data-id="element-1283">
                          <li data-id="element-1284">• Priority level (HIGH, MEDIUM, LOW)</li>
                          <li data-id="element-1285">
                            • Category (Content, Structure, Timing, Targeting)
                          </li>
                          <li data-id="element-1286">• Specific issue description</li>
                          <li data-id="element-1287">• Why it matters (impact explanation)</li>
                          <li data-id="element-1288">• Suggested alternatives or fixes</li>
                          <li data-id="element-1289">• Estimated impact on performance</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Next Steps */}
          <section className="mb-16" data-id="element-1290">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1291">
              What to Do Next
            </h2>

            <p className="text-lg text-gray-600 mb-6" data-id="element-1292">
              Now that you're set up and familiar with the interface, here are
              your next steps:
            </p>

            <div className="space-y-4 mb-8" data-id="element-1293">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border-l-4 border-indigo-500" data-id="element-1294">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1295">
                  1. Review Your Top Opportunities
                </h3>
                <p className="text-gray-700" data-id="element-1296">
                  Go to the Dashboard to see your top 5 flows with the biggest
                  improvement opportunities. These are automatically ranked by
                  potential revenue impact.
                </p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-l-4 border-purple-500" data-id="element-1297">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1298">
                  2. Start with High-Priority Recommendations
                </h3>
                <p className="text-gray-700" data-id="element-1299">
                  Pick one flow with a low health score and focus on
                  implementing its HIGH priority recommendations first. These
                  have the biggest impact with the least effort.
                </p>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-red-50 p-6 rounded-lg border-l-4 border-pink-500" data-id="element-1300">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1301">
                  3. Learn More About Health Scores
                </h3>
                <p className="text-gray-700" data-id="element-1302">
                  Read our comprehensive guide on understanding flow health
                  scores to learn how they're calculated and how to use them
                  effectively.
                </p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500" data-id="element-1303">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1304">
                  4. Implement Your First Recommendation
                </h3>
                <p className="text-gray-700" data-id="element-1305">
                  Follow our guide on implementing AI recommendations to learn
                  the step-by-step process for making changes and tracking their
                  impact.
                </p>
              </div>
            </div>
          </section>

          {/* Common Questions */}
          <section className="mb-16" data-id="element-1306">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1307">
              Common Questions
            </h2>

            <div className="space-y-4" data-id="element-1308">
              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1309">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1310">
                  How long does the initial sync take?
                </h3>
                <p className="text-gray-700" data-id="element-1311">
                  The initial sync typically takes 2-5 minutes depending on how
                  many flows you have. You'll see a progress indicator, and you
                  can navigate away—we'll email you when it's complete.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1312">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1313">
                  Can I disconnect Klaviyo later?
                </h3>
                <p className="text-gray-700" data-id="element-1314">
                  Yes, you can disconnect your Klaviyo account at any time from
                  Settings → Integrations. Your historical data in Flowsense
                  will be preserved, but we'll stop syncing new data.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1315">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1316">
                  What if I have multiple Klaviyo accounts?
                </h3>
                <p className="text-gray-700" data-id="element-1317">
                  You can connect multiple Klaviyo accounts by creating separate
                  clients in Flowsense. Each client can have its own Klaviyo
                  connection.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1318">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1319">
                  Do I need to do anything after connecting?
                </h3>
                <p className="text-gray-700" data-id="element-1320">
                  No! Flowsense automatically syncs your data every 6 hours.
                  Just log in anytime to see updated metrics and new
                  recommendations.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1321">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1322">
                  Can Flowsense modify my Klaviyo flows?
                </h3>
                <p className="text-gray-700" data-id="element-1323">
                  No. Flowsense has read-only access. We can analyze your flows
                  and provide recommendations, but you make all changes directly
                  in Klaviyo.
                </p>
              </div>
            </div>
          </section>

          {/* Related Guides */}
          <section className="border-t border-gray-200 pt-12" data-id="element-1324">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1325">
              Related Guides
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1326">
              <Link to="/guides/understanding-flow-health-scores" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1327">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1328">
                  Understanding Flow Health Scores
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1329">
                  Learn how Flowsense calculates health scores and what each
                  metric means for your flows.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1330">
                  Read Guide →
                </span>
              </Link>
              <Link to="/guides/implementing-ai-recommendations" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1331">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1332">
                  Implementing AI Recommendations
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1333">
                  Learn how to prioritize and implement Flowsense
                  recommendations for maximum ROI.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1334">
                  Read Guide →
                </span>
              </Link>
              <Link to="/guides/agency-white-label-setup" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1335">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1336">
                  Agency White-Label Setup
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1337">
                  Complete guide to setting up custom domains and branded
                  reports for your agency.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1338">
                  Read Guide →
                </span>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <div className="mt-16 bg-gray-900 rounded-2xl p-8 lg:p-12 text-center" data-id="element-1339">
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1340">
              Ready to Get Started?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-1341">
              Create your Flowsense account and start optimizing your Klaviyo
              flows in under 5 minutes.
            </p>
            <a href="https://go.useflowsense.com/signup" data-id="element-1342">
              <Button size="lg" data-id="element-1343">Sign Up Free</Button>
            </a>
          </div>
        </Container>
      </article>
    </div>;
}